/**
 * 树状数组 (Fenwick Tree / Binary Indexed Tree)
 * 用于高效维护数组前缀和，支持 O(log n) 的更新和查询。
 */
export class FenwickTree {
    private tree: Float64Array;
    private size: number;

    constructor(size: number) {
        this.size = size;
        // 树状数组下标从 1 开始，所以长度为 size + 1
        this.tree = new Float64Array(size + 1);
    }

    /**
     * 在指定索引处增加值
     * @param index 索引 (0-based)
     * @param delta 增量
     */
    add(index: number, delta: number): void {
        if (index < 0 || index >= this.size || !Number.isFinite(delta)) return;
        
        let i = index + 1;
        while (i <= this.size) {
            this.tree[i] += delta;
            i += i & -i;
        }
    }

    /**
     * 查询前缀和 [0, index]
     * @param index 索引 (0-based)
     */
    query(index: number): number {
        if (index < 0) return 0;
        let i = Math.min(index, this.size - 1) + 1;
        let sum = 0;
        while (i > 0) {
            sum += this.tree[i];
            i -= i & -i;
        }
        return sum;
    }

    /**
     * 查找第一个前缀和大于等于 value 的索引
     * 常用于通过 scrollTop 查找页码
     * @param value 目标前缀和
     */
    findFirstGreaterOrEqual(value: number): number {
        if (value <= 0) return 0;
        
        let idx = 0;
        let currentSum = 0;
        // 获取最高位的位权
        let bitMask = 1 << Math.floor(Math.log2(this.size));
        
        for (let i = bitMask; i > 0; i >>= 1) {
            const nextIdx = idx + i;
            if (nextIdx <= this.size && currentSum + this.tree[nextIdx] < value) {
                idx = nextIdx;
                currentSum += this.tree[idx];
            }
        }
        
        return idx; // 返回的是 0-based 索引
    }

    /**
     * 获取指定范围的和 [start, end)
     */
    queryRange(start: number, end: number): number {
        return this.query(end - 1) - this.query(start - 1);
    }

    /**
     * 获取单点值
     */
    get(index: number): number {
        return this.queryRange(index, index + 1);
    }

    /**
     * 重置所有值为 0
     */
    clear(): void {
        this.tree.fill(0);
    }
}

